import React, { useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Bell, X, CheckCheck, ExternalLink, Trash2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { cn } from '@elsapiens/utils'
import { Button } from '@elsapiens/ui'
import { useToast } from '../ui'

import { useNotifications, type AppNotification } from '../../hooks/use-notifications'

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

interface PanelProps {
  isOpen: boolean
  onClose: () => void
  notifications: AppNotification[]
  unreadCount: number
  onMarkRead: (id: string) => void
  onMarkAllRead: () => void
  onClearAll: () => void
  onDismissOne: (id: string) => void
}

function NotificationPanel({
  isOpen,
  onClose,
  notifications,
  unreadCount,
  onMarkRead,
  onMarkAllRead,
  onClearAll,
  onDismissOne,
}: PanelProps): React.JSX.Element {
  const navigate = useNavigate()
  const hasUnread = unreadCount > 0
  const hasAny = notifications.length > 0

  const handleClick = (n: AppNotification) => {
    onMarkRead(n.id)
    if (n.deep_link) {
      navigate(n.deep_link)
      onClose()
    }
  }

  // Portal to document.body breaks CSS variable inheritance from el-surface-colored
  // on the topbar header, which would otherwise force --foreground to white.
  return createPortal(
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/30"
          onClick={onClose}
        />
      )}

      {/* Sliding panel */}
      <div
        className={cn(
          'fixed inset-y-0 right-0 z-40 w-80 bg-background text-foreground border-l border-border flex flex-col transform transition-transform duration-200 ease-in-out',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between el-px-md border-b border-border flex-shrink-0"
          style={{ height: 'var(--topbar-height)' }}
        >
          <div className="flex items-center el-gap-sm">
            <Bell className="w-4 h-4 text-muted-foreground" />
            <span className="font-semibold text-sm text-foreground">Notifications</span>
          </div>
          <div className="flex items-center el-gap-xs">
            {hasUnread && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onMarkAllRead}
                leftIcon={<CheckCheck className="w-4 h-4" />}
              >
                Mark all read
              </Button>
            )}
            {hasAny && (
              <button
                onClick={onClearAll}
                className="el-p-xs rounded-md hover:bg-destructive/10 hover:text-destructive text-muted-foreground transition-colors"
                aria-label="Clear all notifications"
                title="Clear all"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={onClose}
              className="el-p-xs rounded-md hover:bg-muted transition-colors"
              aria-label="Close notifications"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Scrollable list */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full el-p-xl text-center">
              <Bell className="w-10 h-10 text-muted-foreground mb-3 opacity-40" />
              <p className="text-sm text-muted-foreground">No notifications yet</p>
            </div>
          ) : (
            <div className="el-p-sm el-space-y-xs">
              {notifications.map(n => (
                <div
                  key={n.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleClick(n)}
                  onKeyDown={e => e.key === 'Enter' && handleClick(n)}
                  className={cn(
                    'w-full text-left rounded-md el-p-sm transition-colors hover:bg-muted group cursor-pointer',
                    !n.read && 'bg-primary/5 hover:bg-primary/10'
                  )}
                >
                  <div className="flex items-start el-gap-sm">
                    <div className="flex-shrink-0 mt-0.5">
                      <span className={cn(
                        'block w-2 h-2 rounded-full',
                        n.read ? 'bg-transparent' : 'bg-primary'
                      )} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={cn(
                        'text-sm leading-snug',
                        n.read ? 'text-muted-foreground' : 'text-foreground font-medium'
                      )}>
                        {n.title}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{n.body}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-[10px] text-muted-foreground">
                          {timeAgo(n.created_at)}
                        </span>
                        {n.deep_link && (
                          <ExternalLink className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        )}
                      </div>
                    </div>
                    {n.read && (
                      <button
                        onClick={e => { e.stopPropagation(); onDismissOne(n.id) }}
                        className="flex-shrink-0 el-p-xs rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                        aria-label="Dismiss notification"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  )
}

export function NotificationBell(): React.JSX.Element {
  const toast = useToast()
  const onNew = useCallback((n: AppNotification) => {
    toast.info(n.body ? `${n.title}: ${n.body}` : n.title)
  }, [toast])
  const { notifications, unreadCount, markRead, markAllRead, clearAll, dismissOne } = useNotifications(onNew)
  const [panelOpen, setPanelOpen] = useState(false)

  return (
    <>
      <button
        onClick={() => setPanelOpen(true)}
        aria-label="Notifications"
        className="relative el-p-xs rounded-md hover:bg-muted transition-colors flex-shrink-0"
      >
        <Bell className="w-4 h-4 text-muted-foreground" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[16px] h-4 px-0.5 rounded-full bg-destructive text-[9px] font-bold text-destructive-foreground leading-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      <NotificationPanel
        isOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        notifications={notifications}
        unreadCount={unreadCount}
        onMarkRead={id => { void markRead(id) }}
        onMarkAllRead={() => { void markAllRead() }}
        onClearAll={() => { void clearAll() }}
        onDismissOne={id => { void dismissOne(id) }}
      />
    </>
  )
}
